import React, { useMemo } from "react";

import { Form, Select, Button } from "antd";
import { MinusCircleOutlined } from "@ant-design/icons";

type Value = string;
type ValueArray = Array<Value | undefined>;

interface Selectable {
  value: Value;
  label: string;
}

interface Props {
  options: Selectable[];
  maxSelected?: number;
  value?: ValueArray;
  onChange?: (v: ValueArray) => void;
  valueLabel?: string;
}

const SelectorList: React.FC<Props> = (props) => {
  // Must be optional to play nice with Ant Design's forms but won't
  // actually work without them
  const assertDefined = (propName: keyof Props) => {
    if (props[propName] === undefined) {
      console.error(`Property '${propName} must be defined for ListSelector`);
    }
  };
  assertDefined("onChange");

  const { value = [], onChange } = props;

  const { add, remove, changeValue } = useMemo(() => {
    const triggerChange = (newValues: ValueArray) => {
      if (onChange) {
        onChange(newValues);
      }
    };

    const add = () => {
      triggerChange(value.concat(undefined));
    };

    const remove = (i: number) => {
      triggerChange([...value.slice(0, i), ...value.slice(i + 1)]);
    };

    const changeValue = (index: number, updatedValue: Value) => {
      const newVals = [...value];
      newVals[index] = updatedValue;
      triggerChange(newVals);
    };

    return { add, remove, changeValue };
  }, [onChange, value]);

  const filteredOptions = (v?: Value) => {
    return props.options.filter(
      (opt) => opt.value === v || !value.includes(opt.value)
    );
  };

  const maxReached = value.length >= (props.maxSelected || Infinity);
  const valueLabel = props.valueLabel ? `${props.valueLabel} ` : "";

  return (
    <div>
      {value.map((v, i) => (
        <Form.Item key={i}>
          <Select
            style={{ width: "95%" }}
            value={v}
            onChange={(v) => changeValue(i, v)}
            options={filteredOptions(v)}
          />
          <MinusCircleOutlined
            style={{ marginLeft: "8px" }}
            onClick={() => remove(i)}
          />
        </Form.Item>
      ))}
      <Button disabled={maxReached} onClick={add} type="dashed">
        {maxReached ? `Max ${valueLabel}Added` : `Add ${valueLabel}`}
      </Button>
    </div>
  );
};

export default SelectorList;
